import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { isApiConfigured } from "../auth-config";
import { postStudentCohortJoin } from "../auth/api-client";
import { loadProfile } from "../auth/auth-session";
import { useAuth } from "../auth/AuthContext";
import {
  clearPostLoginReturnTo,
  loadPostLoginReturnTo,
  resolvePostAuthRoute,
} from "../auth/post-login-redirect";
import {
  clearSignupIntent,
  resolveSignupIntent,
  saveSignupIntent,
  type ResolvedSignupIntent,
} from "../auth/signup-intent";
import { TEACHER_APP_TRIAL_DAYS, TEACHER_TRIAL_SUMMARY } from "../marketing/teacher-billing-copy";
import { applyJoinSessionResponse } from "../auth/join-session";

function SignedInLede({ email }: { email: string }) {
  return (
    <p className="lede">
      Signed in as <strong>{email || "your Google account"}</strong>.
    </p>
  );
}

function AmbiguousOnboarding({
  email,
  onChoose,
}: {
  email: string;
  onChoose: (intent: "teacher" | "student") => void;
}) {
  return (
    <>
      <p className="eyebrow">First visit</p>
      <h1>What brings you here?</h1>
      <SignedInLede email={email} />
      <p className="muted">Pick the option that matches what you are trying to do right now.</p>
      <div className="role-grid">
        <button type="button" className="role-tile" onClick={() => onChoose("student")}>
          <span className="role-title">I have a join code</span>
          <span className="role-desc">Join your teacher&apos;s class cohort and work on assigned lessons.</span>
        </button>
        <button type="button" className="role-tile" onClick={() => onChoose("teacher")}>
          <span className="role-title">I want to teach a class</span>
          <span className="role-desc">Start your free teacher trial and build lessons for your students.</span>
        </button>
      </div>
    </>
  );
}

function StudentOnboarding({
  email,
  error,
  busy,
  onJoin,
  onSkip,
}: {
  email: string;
  error: string | null;
  busy: boolean;
  onJoin: (code: string) => void;
  onSkip: () => void;
}) {
  const [code, setCode] = useState("");

  return (
    <>
      <p className="eyebrow">Student</p>
      <h1>Join your class</h1>
      <SignedInLede email={email} />
      <p className="muted">Enter the join code your teacher shared with you.</p>
      {error ? <p className="warn-banner">{error}</p> : null}
      <form
        className="stack-form"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (!code.trim()) return;
          onJoin(code.trim());
        }}
      >
        <label className="field">
          <span className="field-label">Join code</span>
          <input
            className="input"
            value={code}
            onChange={(ev) => setCode(ev.target.value)}
            placeholder="e.g. 4KJH7Q8WZB"
            autoCapitalize="characters"
            spellCheck={false}
            disabled={busy}
            required
          />
        </label>
        <div className="actions">
          <button type="submit" className="btn primary" disabled={busy || !code.trim()}>
            {busy ? "Joining…" : "Join cohort"}
          </button>
          <button type="button" className="btn secondary" onClick={onSkip} disabled={busy}>
            I&apos;ll join later
          </button>
        </div>
      </form>
      <p className="fineprint">
        Want to teach instead? Visit the <Link to="/">homepage</Link> and start a teacher trial after you
        have joined your class.
      </p>
    </>
  );
}

function TeacherOnboarding({
  email,
  error,
  busy,
  onStartTrial,
}: {
  email: string;
  error: string | null;
  busy: boolean;
  onStartTrial: () => void;
}) {
  return (
    <>
      <p className="eyebrow">Teacher</p>
      <h1>Start your free trial</h1>
      <SignedInLede email={email} />
      <p className="muted">{TEACHER_TRIAL_SUMMARY}</p>
      {error ? <p className="warn-banner">{error}</p> : null}
      <div className="actions">
        <button type="button" className="btn primary" onClick={onStartTrial} disabled={busy}>
          {busy ? "Starting…" : `Start ${TEACHER_APP_TRIAL_DAYS}-day trial`}
        </button>
      </div>
      <p className="fineprint">
        No credit card required. Students always use MsLeonard for free. Need a join code instead?{" "}
        <Link to="/login?returnTo=%2Fjoin">Sign in to join a class</Link>.
      </p>
    </>
  );
}

export default function OnboardingPage() {
  const { identity, profile, getBearerToken, setRole, setEnrollmentSummary, signOut, refresh } =
    useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [localIntent, setLocalIntent] = useState<ResolvedSignupIntent | null>(null);

  if (!identity || !profile) return <Navigate to="/login" replace />;
  if (profile.role !== "pending") {
    return <Navigate to={resolvePostAuthRoute(profile)} replace />;
  }

  const funnel = localIntent ?? resolveSignupIntent(profile, loadPostLoginReturnTo());
  const email = identity.email || "your Google account";

  async function finishStudentJoin(code: string) {
    const bearer = getBearerToken();
    if (!bearer) {
      setError("Sign in again to join your cohort.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await postStudentCohortJoin(bearer, { joinCode: code });
      applyJoinSessionResponse(result);
      if (isApiConfigured()) {
        await setEnrollmentSummary("active");
      }
      clearSignupIntent();
      clearPostLoginReturnTo();
      refresh();
      const next = loadProfile();
      if (next) navigate(resolvePostAuthRoute(next), { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not join cohort");
    } finally {
      setBusy(false);
    }
  }

  async function finishStudentSkip() {
    setBusy(true);
    setError(null);
    try {
      await setRole("student");
      clearSignupIntent();
      const next = loadProfile();
      if (next) navigate(resolvePostAuthRoute(next), { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save role");
    } finally {
      setBusy(false);
    }
  }

  async function finishTeacherTrial() {
    setBusy(true);
    setError(null);
    try {
      await setRole("teacher");
      clearSignupIntent();
      clearPostLoginReturnTo();
      const next = loadProfile();
      if (next) navigate(resolvePostAuthRoute(next), { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start trial");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page">
      <div className="card card-wide">
        {funnel === "ambiguous" ? (
          <AmbiguousOnboarding
            email={email}
            onChoose={(intent) => {
              saveSignupIntent(intent);
              setLocalIntent(intent);
            }}
          />
        ) : funnel === "student" ? (
          <StudentOnboarding
            email={email}
            error={error}
            busy={busy}
            onJoin={(code) => void finishStudentJoin(code)}
            onSkip={() => void finishStudentSkip()}
          />
        ) : (
          <TeacherOnboarding
            email={email}
            error={error}
            busy={busy}
            onStartTrial={() => void finishTeacherTrial()}
          />
        )}

        <div className="actions" style={{ marginTop: funnel === "ambiguous" ? "1rem" : 0 }}>
          <button type="button" className="btn secondary" onClick={() => signOut()} disabled={busy}>
            Sign out
          </button>
        </div>
      </div>
    </main>
  );
}
